/**
 * THE COMPANY'S OWN WORDS FOR ITS PEOPLE AND PLACES — one vocabulary, both
 * languages, every form a sentence needs. Plain data, deliberately zod-free:
 * the web console resolves it before first render, and importing the settings
 * schema for a table of nouns put zod in the entry chunk. `settings.schema.ts`
 * validates the stored shape and re-exports all of this.
 *
 * WHAT IT REPLACED (audit, 2026-09-25). Seven English strings, one per
 * singular/plural, shown unchanged in every language; Arabic reached only by
 * looking the ENGLISH word up in the locale file, so a custom word stayed
 * English in Arabic and the construction set's nav labels were never applied
 * at all. Screens lowercased the words themselves ("sites & camps") and the
 * rail glued English phrases together ("فرقة directory"). The fix is a model,
 * not more call-site care:
 *
 *   - FOUR NOUNS, each with a singular and a plural (`TermForms`), per
 *     language. There is no seventh "workers vs worker" key to keep in step.
 *   - A WORD SET is complete in English AND Arabic, so nothing is translated
 *     by guessing from an English spelling.
 *   - A company's own word is per language and per form. An empty box
 *     inherits the word set's word — never an English word in Arabic.
 *   - THREE READINGS of every form, computed here once (`termReadings`):
 *       label   a heading or a nav item: "Employees" / "الموظفين"
 *       inline  inside a sentence:       "employees" / "موظفين"
 *       def     inside a sentence, with the article: "employees" / "الموظفين"
 *     English puts "the" in the sentence, so its `def` is its `inline`.
 *     Arabic fuses the article to the noun, so a sentence that means "the
 *     workers" asks for `def` instead of writing "الـ{{workers}}" (which gave
 *     "الـ 5 الموظفون" and "الـYard").
 *   - ARABIC PLURALS ARE IN THE OBLIQUE CASE (موظفين, not موظفون): a word
 *     dropped into a sentence is far more often an object or follows a
 *     preposition than it is a subject, and a heading reads either way.
 */

export type TerminologyProfile = 'hr' | 'construction';

/**
 * SEVEN NOUNS (2026-09-29). Four until then, and "employee" did two jobs: the
 * company's people in general AND its office staff. The construction set spelt
 * it "staff", so every sentence about everyone — "terminate the selected
 * employees", a headcount, a leave balance — told a construction company it
 * was about its office only, while the group filter beside it said "Staff" for
 * the office. The owner found the two words on one screen. `staff` is now its
 * own noun (the OFFICE_STAFF group, the people who sign in), `employee` is
 * everyone in every set, and camps and departments — the two other nouns a
 * company most often names its own way — are configurable too.
 */
export const TERM_KEYS = ['employee', 'worker', 'staff', 'worksite', 'camp', 'department', 'team'] as const;
export type TermKey = (typeof TERM_KEYS)[number];

/** The languages a vocabulary carries. The console may show fewer (`SUPPORTED_LOCALES`). */
export const TERM_LANGS = ['en', 'ar'] as const;
export type TermLang = (typeof TERM_LANGS)[number];

export const TERM_FORMS = ['one', 'other'] as const;
export type TermForm = (typeof TERM_FORMS)[number];

/**
 * One noun in one language. `one`/`other` are the in-sentence words.
 * `oneDef`/`otherDef` are Arabic's definite forms, spelled out by a word set
 * where the rule in `arabicDefinite` would get them wrong (a two-word phrase).
 */
export interface TermForms {
  one: string;
  other: string;
  oneDef?: string;
  otherDef?: string;
}

export type LangVocabulary = Record<TermKey, TermForms>;
export type Vocabulary = Record<TermLang, LangVocabulary>;

/** Two-word Arabic phrases, so their definite forms are spelt out (see `arabicDefinite`). */
const AR_STAFF: TermForms = {
  one: 'موظف إداري',
  other: 'موظفين إداريين',
  oneDef: 'الموظف الإداري',
  otherDef: 'الموظفين الإداريين',
};
const AR_CAMP: TermForms = { one: 'سكن عمال', other: 'مساكن عمال', oneDef: 'سكن العمال', otherDef: 'مساكن العمال' };

/**
 * The two word sets. English is written in its in-sentence case — a heading's
 * capital is added by `termReadings`, so a company's "Yard" is never turned
 * into "yard" mid-sentence.
 */
export const WORD_SETS: Record<TerminologyProfile, Vocabulary> = {
  hr: {
    en: {
      employee: { one: 'employee', other: 'employees' },
      worker: { one: 'worker', other: 'workers' },
      staff: { one: 'staff member', other: 'staff' },
      worksite: { one: 'worksite', other: 'worksites' },
      camp: { one: 'camp', other: 'camps' },
      department: { one: 'department', other: 'departments' },
      team: { one: 'team', other: 'teams' },
    },
    ar: {
      employee: { one: 'موظف', other: 'موظفين' },
      worker: { one: 'عامل', other: 'عمال' },
      staff: AR_STAFF,
      worksite: { one: 'موقع عمل', other: 'مواقع عمل', oneDef: 'موقع العمل', otherDef: 'مواقع العمل' },
      camp: AR_CAMP,
      department: { one: 'قسم', other: 'أقسام' },
      team: { one: 'فريق', other: 'فرق' },
    },
  },
  construction: {
    en: {
      employee: { one: 'employee', other: 'employees' },
      worker: { one: 'worker', other: 'workers' },
      staff: { one: 'staff member', other: 'staff' },
      worksite: { one: 'site', other: 'sites' },
      camp: { one: 'camp', other: 'camps' },
      department: { one: 'department', other: 'departments' },
      team: { one: 'crew', other: 'crews' },
    },
    ar: {
      employee: { one: 'موظف', other: 'موظفين' },
      worker: { one: 'عامل', other: 'عمال' },
      staff: AR_STAFF,
      worksite: { one: 'موقع', other: 'مواقع' },
      camp: AR_CAMP,
      department: { one: 'قسم', other: 'أقسام' },
      team: { one: 'طاقم', other: 'طواقم' },
    },
  },
};

/** A company's own words: any language, any noun, any form — each optional. */
export type TermOverrides = Partial<
  Record<TermLang, Partial<Record<TermKey, Partial<Record<TermForm, string | undefined>>>>>
>;

/**
 * The stored shape `resolveVocabulary` reads. Structural rather than
 * `z.infer`, so this module stays zod-free; `settings.schema.ts` asserts at
 * compile time that its output is exactly this.
 */
export interface TerminologySettingsInput {
  profile: TerminologyProfile;
  overrides?: TermOverrides;
}

/** The word set, with every non-blank company word laid over it. */
export function resolveVocabulary(setting: TerminologySettingsInput): Vocabulary {
  const base = WORD_SETS[setting.profile] ?? WORD_SETS.hr;
  const out = {} as Vocabulary;
  for (const lang of TERM_LANGS) {
    out[lang] = {} as LangVocabulary;
    for (const key of TERM_KEYS) {
      const own = setting.overrides?.[lang]?.[key] ?? {};
      const forms: TermForms = { ...base[lang][key] };
      for (const form of TERM_FORMS) {
        const word = own[form]?.trim();
        if (!word) continue;
        forms[form] = word;
        // The word set's spelled-out definite belongs to ITS word, not this one.
        delete forms[form === 'one' ? 'oneDef' : 'otherDef'];
      }
      out[lang][key] = forms;
    }
  }
  return out;
}

const ARABIC_LETTER = /^[ء-ي]/;

/**
 * Arabic's definite form of a word the word set did not spell out: one Arabic
 * word takes ال; anything else — a phrase, a Latin-script brand, a word that
 * already has it — is left as written, because guessing where the article goes
 * in a phrase is how "الموقع عمل" happens.
 */
export function arabicDefinite(word: string): string {
  const w = word.trim();
  if (w.includes(' ') || !ARABIC_LETTER.test(w) || w.startsWith('ال')) return w;
  return `ال${w}`;
}

/** First letter up, the rest as written ("sites & camps" → "Sites & camps"). */
function capitalise(word: string): string {
  return word.charAt(0).toLocaleUpperCase('en') + word.slice(1);
}

/** The three readings of one form, in one language — see the header. */
export interface TermReading {
  label: string;
  inline: string;
  def: string;
}

export function termReadings(forms: TermForms, form: TermForm, lang: TermLang): TermReading {
  const word = forms[form];
  if (lang === 'ar') {
    const def = (form === 'one' ? forms.oneDef : forms.otherDef) ?? arabicDefinite(word);
    return { label: def, inline: word, def };
  }
  return { label: capitalise(word), inline: word, def: word };
}

/**
 * A form's name in copy: `employee` / `employees`, `team` / `teams`. "Staff"
 * is its own plural, so its singular is named for the member.
 */
const NAMES: Partial<Record<TermKey, [string, string]>> = { staff: ['staffMember', 'staff'] };

export function termName(key: TermKey, form: TermForm): string {
  const [one, other] = NAMES[key] ?? [key, `${key}s`];
  return form === 'one' ? one : other;
}

/**
 * Every variable a translated string may use, for one language — written in
 * copy under the `term.` namespace:
 *
 *   {{term.employee}} {{term.employees}}         inline — mid-sentence
 *   {{term.Employee}} {{term.Employees}}         label  — a heading, a nav item, a sentence start
 *   {{term.employeeDef}} {{term.employeesDef}}   def    — mid-sentence, with the article
 *
 * …and the same for worker(s), staffMember/staff, worksite(s), camp(s),
 * department(s), team(s). Registered as i18n
 * default variables (`apps/web/src/lib/terminology.ts`), so a string reaches
 * the company's words without its screen passing them.
 */
export function termVariables(vocab: LangVocabulary, lang: TermLang): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const key of TERM_KEYS) {
    for (const form of TERM_FORMS) {
      const name = termName(key, form);
      // A vocabulary resolved before a noun existed (a cached payload, an
      // older server) reads the office word for it, never `undefined`.
      const r = termReadings(vocab[key] ?? WORD_SETS.hr[lang][key], form, lang);
      vars[name] = r.inline;
      vars[capitalise(name)] = r.label;
      vars[`${name}Def`] = r.def;
    }
  }
  return vars;
}

/** The variable names `termVariables` produces — for guards over the copy. */
export const TERM_VARIABLE_NAMES: readonly string[] = Object.keys(
  termVariables(WORD_SETS.hr.en, 'en')
);

/* ---------------------------------------------------------------------------
 * THE SHAPE THIS REPLACED, AND ITS ONE-WAY MIGRATION.
 *
 * Until 2026-09-25 overrides were seven flat English labels —
 * `{ employees: 'Staff', employee: 'Staff member', …, team: 'Crew' }`. They
 * were headings ("Labourers"), so they come across as English in-sentence
 * words ("labourers"): the first letter lowered unless the word is an
 * acronym. A key the new shape already has is never overwritten, because the
 * server deep-merges a PATCH over what is stored, and a half-migrated row can
 * carry both.
 * ------------------------------------------------------------------------- */
const LEGACY_KEYS: Record<string, [TermKey, TermForm]> = {
  employees: ['employee', 'other'],
  employee: ['employee', 'one'],
  workers: ['worker', 'other'],
  worker: ['worker', 'one'],
  worksites: ['worksite', 'other'],
  worksite: ['worksite', 'one'],
  team: ['team', 'one'],
};

function asInlineEnglish(label: string): string {
  const w = label.trim();
  return /^[A-Z][a-z]/.test(w) ? w.charAt(0).toLowerCase() + w.slice(1) : w;
}

export function migrateLegacyOverrides(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const src = raw as Record<string, unknown>;
  const legacy = Object.keys(src).filter((k) => k in LEGACY_KEYS);
  if (legacy.length === 0) return raw;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(src)) if (!(k in LEGACY_KEYS)) out[k] = v;
  const en = { ...((out.en as Record<string, Record<string, unknown>> | undefined) ?? {}) };
  for (const k of legacy) {
    const value = src[k];
    if (typeof value !== 'string' || !value.trim()) continue;
    const [key, form] = LEGACY_KEYS[k]!;
    const slot = { ...(en[key] ?? {}) };
    if (slot[form] === undefined) slot[form] = asInlineEnglish(value);
    en[key] = slot;
  }
  out.en = en;
  return out;
}
